import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ListChecks, Map, Presentation } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Scorecard from "../components/Scorecard";
import SeoHead from "../components/SeoHead";
import { DOMAINS, MATURITY } from "../data/site";

const OUTPUTS = [
  { icon: FileText, title: "Executive Business Health Scorecard", desc: "An honest maturity rating across seven operational domains." },
  { icon: ListChecks, title: "Prioritized Operational Findings", desc: "Evidence-based findings, ranked by business impact — what deserves attention first." },
  { icon: Map, title: "Practical 90-Day Roadmap", desc: "Sequenced priorities your business can actually absorb while it keeps running." },
  { icon: Presentation, title: "Executive Findings Readout", desc: "A direct walkthrough of what the evidence shows and what to do about it." },
];

const ROADMAP = [
  { window: "Days 1–30", focus: "Stabilize", items: ["Install leadership cadence", "Make the core numbers visible", "Address the highest-priority finding"] },
  { window: "Days 31–60", focus: "Standardize", items: ["Document the work that repeats", "Tighten scheduling & dispatch", "Formalize job costing"] },
  { window: "Days 61–90", focus: "Scale", items: ["Launch continuous-improvement loop", "Grow recurring revenue motion", "Hand systems to the team"] },
];

export default function Assessment() {
  return (
    <div data-testid="assessment-page" className="pt-[72px]">
      <SeoHead
        title="Operational Performance Assessment | Local Proof STL"
        description="An evidence-based view of operational health: Executive Business Health Scorecard, prioritized operational findings, a practical 90-day roadmap, and an executive findings readout."
        canonical="https://localproofstl.com/assessment"
        ogType="website"
        ogUrl="https://localproofstl.com/assessment"
        ogImage="https://localproofstl.com/og-image.jpg"
        twitterImage="https://localproofstl.com/og-image.jpg"
      />
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
        <Reveal>
          <p className="eyebrow mb-8">The assessment</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
            The Operational Performance Assessment.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-charcoal/80 leading-relaxed">
            An evidence-based view of operational health across every domain that determines
            whether your business can scale — and a clear, prioritized picture of what deserves
            attention first.
          </p>
        </Reveal>
      </section>

      {/* What you receive */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-2xl overflow-hidden" data-testid="assessment-outputs">
          {OUTPUTS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.04}>
              <div className="bg-white p-7 h-full min-h-[180px] flex flex-col">
                <o.icon size={22} className="text-steel" strokeWidth={1.5} />
                <p className="mt-6 font-serif text-xl text-ink leading-snug">{o.title}</p>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 pb-24">
        <Reveal><Scorecard /></Reveal>
      </section>

      {/* Maturity scale */}
      <section className="bg-surface border-y border-border py-24">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl mb-14">
            <p className="eyebrow mb-6">Maturity model</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
              Five levels, from ad hoc to optimizing.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {MATURITY.map((m, i) => (
              <Reveal key={m.level} delay={i * 0.06}>
                <div className="bg-white border border-border rounded-xl p-6 h-full">
                  <span className="font-serif text-5xl text-ink">{m.level}</span>
                  <p className="mt-3 font-semibold text-charcoal">{m.label}</p>
                  <p className="text-sm text-steel mt-1 leading-snug">{m.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Domains */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-24">
        <Reveal className="max-w-2xl mb-14">
          <p className="eyebrow mb-6">What we assess</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
            Seven operational domains.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-2xl overflow-hidden">
          {DOMAINS.map((d, i) => (
            <Reveal key={d.key} delay={i * 0.04}>
              <div className="bg-white p-8 h-full hover:bg-surface transition-colors duration-300">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-serif text-2xl text-ink">{d.key}</h3>
                  <span className="text-steel font-semibold tabular-nums">{d.score.toFixed(1)}</span>
                </div>
                <p className="text-sm text-charcoal/70 leading-relaxed">{d.blurb}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Roadmap */}
      <section className="bg-ink text-white relative overflow-hidden">
        <div className="noise-overlay" />
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <Reveal className="max-w-2xl mb-16">
            <p className="eyebrow text-mist mb-6">From findings to progress</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-tight tracking-tight">
              A practical 90-day roadmap.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.window} delay={i * 0.08}>
                <div className="border border-white/15 rounded-2xl p-8 h-full hover:bg-white/5 transition-colors duration-300">
                  <p className="text-mist text-sm font-semibold">{r.window}</p>
                  <h3 className="font-serif text-3xl mt-2 mb-6">{r.focus}</h3>
                  <ul className="space-y-3">
                    {r.items.map((it) => (
                      <li key={it} className="text-cloud/80 text-sm leading-snug pb-3 border-b border-white/10">{it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-14">
            <Link
              to="/contact"
              data-testid="assessment-cta-button"
              data-track="request-assessment-page"
              className="group inline-flex items-center gap-2 bg-white text-ink px-7 py-4 rounded-full text-sm font-semibold hover:bg-cloud transition-colors duration-200"
            >
              Request an Assessment
              <ArrowUpRight size={17} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
